import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  ArrowRight,
  Check,
  ChevronLeft,
  MapPin,
  ShieldCheck,
  Clock,
  MessageSquare,
} from 'lucide-react';
import { useBranding } from '../../context/BrandingContext';
import {
  InquiryEstimate,
  InquiryReceipt,
  InquirySubmission,
  InquiryTrip,
  inquiryError,
  inquiryLabel,
  inquiryMoney,
  tripInquiryAPI,
} from '../../services/tripInquiry';
import { inquiryDateTime } from '../../utils/inquiryDateTime';
import './TripInquiryPage.css';

const BookingAssistantChat = lazy(
  () => import('../../components/booking-assistant/BookingAssistantChat')
);

const attemptKey = `modal-trip-inquiry-v1:${window.location.host}`;
function readAttempt(): InquirySubmission | null {
  try {
    const value = sessionStorage.getItem(attemptKey);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}
const initialFields = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  pickupLocation: '',
  dropoffLocation: '',
  start: '',
  end: '',
  passengerCount: '',
  vehicleType: '',
  serviceType: 'one_way',
  serviceNotes: '',
};
type Fields = typeof initialFields;
export function buildInquiryTrip(
  fields: Fields,
  timezone: string,
  stops: string[]
): InquiryTrip {
  const startDate = inquiryDateTime(fields.start, timezone);
  const endDate = inquiryDateTime(fields.end, timezone);
  if (endDate && !startDate)
    throw new Error('Add a departure time before the service end.');
  if (startDate && endDate && new Date(startDate) >= new Date(endDate))
    throw new Error('Service end must be after departure.');
  return {
    timezone,
    pickupLocation: fields.pickupLocation.trim(),
    dropoffLocation: fields.dropoffLocation.trim(),
    startDate,
    endDate,
    passengerCount: fields.passengerCount
      ? Number(fields.passengerCount)
      : undefined,
    vehicleType: fields.vehicleType || undefined,
    serviceType: fields.serviceType,
    serviceNotes: fields.serviceNotes.trim() || undefined,
    stops: stops.map((stop) => stop.trim()).filter(Boolean),
  };
}
export function InquiryEstimateSummary({
  estimate,
}: {
  estimate: InquiryEstimate;
}) {
  return (
    <div className="inquiry-estimate" aria-live="polite">
      <p className="inquiry-eyebrow">
        {estimate.pricing ? 'INDICATIVE ESTIMATE' : 'TEAM REVIEW NEEDED'}
      </p>
      {estimate.pricing ? (
        <>
          <strong className="inquiry-estimate-total">
            {inquiryMoney(Number(estimate.pricing.total))}
          </strong>
          <dl>
            {[
              ['Service', estimate.pricing.subtotal],
              ['Service charge', estimate.pricing.serviceCharge],
              ['Gratuity', estimate.pricing.gratuity],
              ['Tax', estimate.pricing.tax],
            ].map(([label, amount]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{inquiryMoney(Number(amount))}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <p>The booking team will prepare pricing after reviewing your trip.</p>
      )}
      {estimate.notices.map((notice, index) => (
        <p key={index}>{notice}</p>
      ))}
      <p className="inquiry-small">
        Estimate only. No vehicle is held and your trip is not confirmed.
      </p>
    </div>
  );
}
export default function TripInquiryPage() {
  const branding = useBranding();
  const [chatOpen, setChatOpen] = useState(false);
  const [fields, setFields] = useState(initialFields);
  const [stops, setStops] = useState<string[]>([]);
  const [step, setStep] = useState(1);
  const [attempt, setAttempt] = useState<InquirySubmission | null>(readAttempt);
  const [receipt, setReceipt] = useState<InquiryReceipt | null>(null);
  const [trip, setTrip] = useState<InquiryTrip | null>(null);
  const [error, setError] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const form = useQuery(
    ['public-trip-form', window.location.host],
    async () => (await tripInquiryAPI.form()).data,
    { retry: 1 }
  );
  const estimate = useMutation((itinerary: InquiryTrip) =>
    tripInquiryAPI.estimate(itinerary).then((response) => response.data)
  );
  const submit = useMutation(
    (payload: InquirySubmission) =>
      tripInquiryAPI.submit(payload).then((response) => response.data),
    {
      onSuccess: (result) => {
        setReceipt(result);
        try {
          sessionStorage.removeItem(attemptKey);
        } catch {}
      },
      onError: (failure) => setError(inquiryError(failure)),
    }
  );
  useEffect(() => {
    document.title = `${
      form.data?.companyName || 'Your booking team'
    } | Trip inquiry`;
  }, [form.data?.companyName]);
  useEffect(() => {
    if (step > 1 || receipt) heading.current?.focus();
  }, [step, receipt]);
  const update = (name: keyof Fields, value: string) => {
    setFields((current) => ({ ...current, [name]: value }));
    setError('');
  };
  const companyName =
    form.data?.companyName || branding.app_name || 'Your booking team';
  const timezone = form.data?.timezone || 'UTC';
  const advance = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    try {
      if (step === 1) {
        const next = buildInquiryTrip(fields, timezone, stops);
        setTrip(next);
        setStep(2);
      } else if (step === 2) {
        setStep(3);
        if (
          form.data?.mode === 'estimate' &&
          trip?.startDate &&
          trip.endDate &&
          trip.vehicleType
        )
          estimate.mutate(trip);
      }
    } catch (failure) {
      setError((failure as Error).message);
    }
  };
  const send = () => {
    if (!trip) return;
    const payload = attempt || {
      submissionId: crypto.randomUUID(),
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      email: fields.email.trim(),
      phone: fields.phone.trim() || undefined,
      companyName: fields.companyName.trim() || undefined,
      trip,
      mode: form.data?.mode,
    };
    setAttempt(payload);
    try {
      sessionStorage.setItem(attemptKey, JSON.stringify(payload));
    } catch {}
    setError('');
    submit.mutate(payload);
  };
  return (
    <div className="trip-inquiry-shell">
      <header className="inquiry-header">
        <a href="/reserve" className="inquiry-company">
          {branding.logo_url ? (
            <img src={branding.logo_url} crossOrigin="anonymous" alt="" />
          ) : (
            <span className="inquiry-monogram">{companyName.slice(0, 1)}</span>
          )}
          <span>{companyName}</span>
        </a>
        <a href="/customer/access">
          Existing booking <ArrowRight size={15} />
        </a>
      </header>
      <main className="inquiry-main">
        <div className="inquiry-intro">
          <p className="inquiry-eyebrow">
            {companyName.toUpperCase()} · TRIP REQUESTS
          </p>
          <h1>
            Good trips start
            <br />
            with a clear plan.
          </h1>
          <p>
            Tell us where you’re going and who’s coming.
            <br className="inquiry-desktop" /> We’ll help put the right
            transportation in place.
          </p>
        </div>
        {form.data?.enabled && form.data.chatEnabled ? (
          <div className="inquiry-chat-launch">
            <div>
              <MessageSquare size={21} />
              <span>
                <strong>Have a trip in mind?</strong> Describe it in a
                conversation and build your request together.
              </span>
            </div>
            <button type="button" onClick={() => setChatOpen(true)}>
              Plan with our booking assistant <ArrowRight size={17} />
            </button>
          </div>
        ) : null}
        {chatOpen && form.data?.enabled && form.data.chatEnabled ? (
          <Suspense
            fallback={
              <div role="status" className="inquiry-note">
                Opening your booking assistant…
              </div>
            }
          >
            <BookingAssistantChat
              open={chatOpen}
              onClose={() => setChatOpen(false)}
            />
          </Suspense>
        ) : null}
        {form.isLoading ? (
          <div className="inquiry-state" role="status">
            Loading your booking team’s form…
          </div>
        ) : form.error ? (
          <div className="inquiry-state" role="alert">
            <h2>We couldn’t load the inquiry form.</h2>
            <p>{inquiryError(form.error)}</p>
            <button onClick={() => form.refetch()}>
              Try again <ArrowRight size={18} />
            </button>
          </div>
        ) : !form.data?.enabled ? (
          <div className="inquiry-state">
            <h2>Online inquiries are currently closed.</h2>
            <p>Please contact {companyName} directly to discuss your trip.</p>
            <a href="/customer/access">
              Access an existing booking <ArrowRight size={18} />
            </a>
          </div>
        ) : receipt ? (
          <div className="inquiry-success" role="status">
            <span className="inquiry-success-icon">
              <Check size={30} />
            </span>
            <p className="inquiry-eyebrow">YOUR INQUIRY IS SAVED</p>
            <h2>We’ve got your trip request.</h2>
            <p>
              Your reference is <strong>{receipt.reference}</strong>. Keep it
              handy when contacting the booking team.
            </p>
            <div className="inquiry-note">
              <strong>What happens next</strong>
              <p>
                {companyName} will review your itinerary and contact you about
                availability, pricing, and the next steps. Your inquiry is not a
                confirmed reservation.
              </p>
              <p className="inquiry-small">
                {receipt.notificationStatus === 'queued'
                  ? 'Your acknowledgment is queued for email delivery.'
                  : 'Email delivery is being handled separately from your saved inquiry.'}
              </p>
            </div>
            <a href="/customer/access">
              Open customer access <ArrowRight size={18} />
            </a>
          </div>
        ) : attempt ? (
          <div className="inquiry-state">
            <p className="inquiry-eyebrow">PENDING SUBMISSION</p>
            <h2>
              {submit.isLoading
                ? 'Saving your inquiry…'
                : 'Finish saving your request.'}
            </h2>
            <p>
              {attempt.trip.pickupLocation} → {attempt.trip.dropoffLocation}
            </p>
            <p>
              We’ll use the same submission reference when retrying, so a lost
              response won’t create a second inquiry.
            </p>
            {error && (
              <p role="alert" className="inquiry-error">
                {error} Your request may already be saved. Retry to retrieve its
                confirmation.
              </p>
            )}
            <button
              disabled={submit.isLoading}
              onClick={() => {
                setError('');
                submit.mutate(attempt);
              }}
            >
              {submit.isLoading ? 'Saving…' : 'Retry submission'}{' '}
              <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <div className="inquiry-layout">
            <section className="inquiry-form-panel">
              <nav className="inquiry-progress" aria-label="Request steps">
                {['Your trip', 'Contact', 'Review'].map((label, index) => (
                  <span
                    key={label}
                    aria-current={step === index + 1 ? 'step' : undefined}
                    className={
                      step === index + 1
                        ? 'current'
                        : step > index + 1
                        ? 'complete'
                        : ''
                    }
                  >
                    <b>
                      {step > index + 1 ? <Check size={14} /> : `0${index + 1}`}
                    </b>
                    {label}
                  </span>
                ))}
              </nav>
              <form onSubmit={advance}>
                <h2 ref={heading} tabIndex={-1}>
                  {step === 1
                    ? '01 / Your trip'
                    : step === 2
                    ? '02 / Your contact details'
                    : '03 / Review your request'}
                </h2>
                {step === 1 && (
                  <>
                    <p className="inquiry-help">
                      A few details help us plan the right service. It’s okay if
                      your timing isn’t final yet.
                    </p>
                    <div className="inquiry-row">
                      <label>
                        Service type
                        <select
                          value={fields.serviceType}
                          onChange={(event) =>
                            update('serviceType', event.target.value)
                          }
                        >
                          <option value="one_way">
                            One-way transportation
                          </option>
                          <option value="round_trip">Round trip</option>
                          <option value="hourly">Hourly / event service</option>
                        </select>
                      </label>
                      <label>
                        Passengers
                        <input
                          type="number"
                          min="1"
                          max="10000"
                          step="1"
                          value={fields.passengerCount}
                          onChange={(event) =>
                            update('passengerCount', event.target.value)
                          }
                          placeholder="How many travelers?"
                        />
                      </label>
                    </div>
                    <label>
                      Pickup location
                      <input
                        required
                        maxLength={500}
                        value={fields.pickupLocation}
                        onChange={(event) =>
                          update('pickupLocation', event.target.value)
                        }
                        placeholder="Address, venue, or city"
                        autoComplete="off"
                      />
                    </label>
                    {stops.map((stop, index) => (
                      <div className="inquiry-stop" key={index}>
                        <label>
                          Stop {index + 1}
                          <input
                            required
                            maxLength={500}
                            value={stop}
                            onChange={(event) =>
                              setStops((current) =>
                                current.map((value, i) =>
                                  i === index ? event.target.value : value
                                )
                              )
                            }
                          />
                        </label>
                        <button
                          type="button"
                          className="inquiry-text-button"
                          onClick={() =>
                            setStops((current) =>
                              current.filter((_, i) => i !== index)
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {stops.length < 20 && (
                      <button
                        type="button"
                        className="inquiry-text-button"
                        onClick={() => setStops((current) => [...current, ''])}
                      >
                        + Add a stop
                      </button>
                    )}
                    <label>
                      Destination
                      <input
                        required
                        maxLength={500}
                        value={fields.dropoffLocation}
                        onChange={(event) =>
                          update('dropoffLocation', event.target.value)
                        }
                        placeholder="Where are you headed?"
                        autoComplete="off"
                      />
                    </label>
                    <div className="inquiry-row">
                      <label>
                        Departure <span>(optional)</span>
                        <input
                          type="datetime-local"
                          value={fields.start}
                          onChange={(event) =>
                            update('start', event.target.value)
                          }
                        />
                      </label>
                      <label>
                        {fields.serviceType === 'round_trip'
                          ? 'Return / service end'
                          : 'Service end'}{' '}
                        <span>(optional)</span>
                        <input
                          type="datetime-local"
                          value={fields.end}
                          onChange={(event) =>
                            update('end', event.target.value)
                          }
                        />
                      </label>
                    </div>
                    <p className="inquiry-small">
                      <Clock size={14} /> All times are in{' '}
                      {timezone.replace(/_/g, ' ')}.
                    </p>
                    <label>
                      Vehicle preference <span>(optional)</span>
                      <select
                        value={fields.vehicleType}
                        onChange={(event) =>
                          update('vehicleType', event.target.value)
                        }
                      >
                        <option value="">Let the team recommend</option>
                        {form.data.vehicleTypes.map((type) => (
                          <option key={type} value={type}>
                            {inquiryLabel(type)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Trip details and accessibility needs{' '}
                      <span>(optional)</span>
                      <textarea
                        rows={4}
                        maxLength={4000}
                        value={fields.serviceNotes}
                        onChange={(event) =>
                          update('serviceNotes', event.target.value)
                        }
                        placeholder="Tell us about luggage, event timing, or accommodations you’d like to discuss. Please avoid sensitive medical details."
                      />
                    </label>
                  </>
                )}
                {step === 2 && (
                  <>
                    <p className="inquiry-help">
                      Who should our booking team contact about this trip?
                    </p>
                    <div className="inquiry-row">
                      <label>
                        First name
                        <input
                          autoComplete="given-name"
                          required
                          maxLength={100}
                          value={fields.firstName}
                          onChange={(event) =>
                            update('firstName', event.target.value)
                          }
                        />
                      </label>
                      <label>
                        Last name
                        <input
                          autoComplete="family-name"
                          required
                          maxLength={100}
                          value={fields.lastName}
                          onChange={(event) =>
                            update('lastName', event.target.value)
                          }
                        />
                      </label>
                    </div>
                    <label>
                      Email address
                      <input
                        type="email"
                        autoComplete="email"
                        required
                        maxLength={254}
                        value={fields.email}
                        onChange={(event) =>
                          update('email', event.target.value)
                        }
                        placeholder="you@example.com"
                      />
                    </label>
                    <div className="inquiry-row">
                      <label>
                        Phone <span>(optional)</span>
                        <input
                          type="tel"
                          autoComplete="tel"
                          maxLength={30}
                          value={fields.phone}
                          onChange={(event) =>
                            update('phone', event.target.value)
                          }
                        />
                      </label>
                      <label>
                        Organization <span>(optional)</span>
                        <input
                          autoComplete="organization"
                          maxLength={160}
                          value={fields.companyName}
                          onChange={(event) =>
                            update('companyName', event.target.value)
                          }
                        />
                      </label>
                    </div>
                    <div className="inquiry-note">
                      <ShieldCheck size={20} />
                      <p>
                        We’ll use these details to respond to your request. No
                        payment information is needed.
                      </p>
                    </div>
                  </>
                )}
                {step === 3 && (
                  <>
                    <p className="inquiry-help">
                      Check your details before sending them to {companyName}.
                    </p>
                    <dl className="inquiry-review">
                      <div>
                        <dt>Route</dt>
                        <dd>
                          {fields.pickupLocation} → {fields.dropoffLocation}
                          {stops.length > 0 && (
                            <small>Via {stops.join(' · ')}</small>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Travelers & service</dt>
                        <dd>
                          {fields.passengerCount || 'To be discussed'}{' '}
                          passengers · {inquiryLabel(fields.serviceType)}
                          {fields.vehicleType &&
                            ` · ${inquiryLabel(fields.vehicleType)}`}
                        </dd>
                      </div>
                      <div>
                        <dt>Timing</dt>
                        <dd>
                          {fields.start.replace('T', ' ') || 'To be discussed'}
                          {fields.end && ` → ${fields.end.replace('T', ' ')}`}
                          <small>{timezone}</small>
                        </dd>
                      </div>
                      <div>
                        <dt>Contact</dt>
                        <dd>
                          {fields.firstName} {fields.lastName}
                          <small>
                            {fields.email}
                            {fields.phone && ` · ${fields.phone}`}
                            {fields.companyName && ` · ${fields.companyName}`}
                          </small>
                        </dd>
                      </div>
                      {fields.serviceNotes && (
                        <div>
                          <dt>Details</dt>
                          <dd>{fields.serviceNotes}</dd>
                        </div>
                      )}
                    </dl>
                    {estimate.isLoading && (
                      <p role="status">Checking an indicative estimate…</p>
                    )}
                    {estimate.data && (
                      <InquiryEstimateSummary estimate={estimate.data} />
                    )}
                    {estimate.error && (
                      <div className="inquiry-note">
                        <p>
                          Pricing is unavailable right now. Your inquiry can
                          still be sent for a team quote.
                        </p>
                        <button
                          type="button"
                          className="inquiry-text-button"
                          onClick={() => trip && estimate.mutate(trip)}
                        >
                          Retry estimate
                        </button>
                      </div>
                    )}
                    {form.data.mode === 'estimate' &&
                      !estimate.data &&
                      !estimate.isLoading &&
                      !estimate.error && (
                        <div className="inquiry-note">
                          <p>
                            Add departure, service end, and a vehicle preference
                            to see an estimate, or submit now for a team quote.
                          </p>
                        </div>
                      )}
                    <p className="inquiry-small">
                      Sending a request does not hold inventory or confirm a
                      reservation. You’ll review the quote, service terms, and
                      payment requirements with the booking team.
                    </p>
                  </>
                )}
                {error && (
                  <p role="alert" className="inquiry-error">
                    {error}
                  </p>
                )}
                <div className="inquiry-actions">
                  {step > 1 && (
                    <button
                      type="button"
                      className="inquiry-back"
                      onClick={() => {
                        estimate.reset();
                        setStep(step - 1);
                      }}
                    >
                      <ChevronLeft size={17} /> Back
                    </button>
                  )}
                  {step < 3 ? (
                    <button type="submit">
                      {step === 1 ? 'Continue to contact' : 'Review request'}{' '}
                      <ArrowRight size={19} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={estimate.isLoading || submit.isLoading}
                      onClick={send}
                    >
                      Send trip inquiry <ArrowRight size={19} />
                    </button>
                  )}
                </div>
              </form>
            </section>
            <aside className="inquiry-sidebar">
              <p className="inquiry-eyebrow">WHAT HAPPENS NEXT</p>
              <ol>
                <li>
                  <span>01</span>
                  <div>
                    <h3>Tell us your plan.</h3>
                    <p>
                      Share your route, travelers, and any details that matter.
                    </p>
                  </div>
                </li>
                <li>
                  <span>02</span>
                  <div>
                    <h3>We work through the details.</h3>
                    <p>
                      Your booking team reviews service, availability, and
                      pricing.
                    </p>
                  </div>
                </li>
                <li>
                  <span>03</span>
                  <div>
                    <h3>Review, then confirm.</h3>
                    <p>
                      Approve your quote and complete the required arrangements
                      before your trip is confirmed.
                    </p>
                  </div>
                </li>
              </ol>
              <div className="inquiry-sidebar-note">
                <MapPin size={22} />
                <p>
                  {form.data.notice ||
                    'A request today. A better-planned trip tomorrow.'}
                </p>
              </div>
              <p className="inquiry-small">
                Already working with us?{' '}
                <a href="/customer/access">Access your customer portal →</a>
              </p>
            </aside>
          </div>
        )}
      </main>
      <footer className="inquiry-footer">
        <span>{companyName}</span>
        <span>
          Connected by <strong>MODAL</strong>
        </span>
      </footer>
    </div>
  );
}
